import { useAppSelector } from '../../../../redux/hooks';
import { useState } from 'react';
import {
  IDataModelListCdwTableColumnsQuery,
  IDataModelListCdwTableColumnsQueryVariables,
  IDataModelListSchemaTableColumnsQuery,
  IDataModelListSchemaTableColumnsQueryVariables,
  ITableColumnFieldsFragment,
  ITableColumnPaginated,
} from '../../../../utils/apollo/graphqlSchema';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../../../redux/selectors/dataModeler';
import { assertNotNil } from '../../../../utils/types';
import { useQuery } from '@apollo/client';
import {
  DATA_MODEL_LIST_CDW_TABLE_COLUMNS,
  DATA_MODEL_LIST_SCHEMA_TABLE_COLUMNS,
} from '../../../../utils/apollo/dataModeler/queries';
import { ISortableTableItem } from './types';

interface IProps {
  tableItem: ISortableTableItem;
}

const useDataModelerTableExpandedModel = ({ tableItem }: IProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const [availableColumnsFromServer, setAvailableColumnsFromServer] = useState<
    ITableColumnFieldsFragment[]
  >([]);
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  assertNotNil(schemaObj);
  const draftEdits = useAppSelector(dataModelerSelectCurrentDraftEdits);

  const { tableKey, viewKey, sqlTableName } = tableItem;

  const onLoadColumnsCompleted = (data: ITableColumnPaginated) => {
    const { entities, errors } = data;
    if (errors.length > 0) {
      setServerError(errors.join('\n'));
      setAvailableColumnsFromServer([]);
    } else {
      setAvailableColumnsFromServer(entities);
    }
  };

  // this is called for existing schemas. Results will include custom columns
  const { loading: loadingSchemaColumns } = useQuery<
    IDataModelListSchemaTableColumnsQuery,
    IDataModelListSchemaTableColumnsQueryVariables
  >(DATA_MODEL_LIST_SCHEMA_TABLE_COLUMNS, {
    variables: {
      schemaKey: schemaObj.key,
      viewKey,
      tableKey,
      sqlTableName,
      connection: draftEdits?.dataSource?.connection ?? null,
    },
    skip: !!draftEdits?.isNew,
    onCompleted: (resp) =>
      onLoadColumnsCompleted(resp.dataModelListSchemaTableColumns),
  });

  // this is called for new (draft) schemas, when we ony have connection info
  const { loading: loadingConnectionColumns } = useQuery<
    IDataModelListCdwTableColumnsQuery,
    IDataModelListCdwTableColumnsQueryVariables
  >(DATA_MODEL_LIST_CDW_TABLE_COLUMNS, {
    variables: {
      connection: draftEdits?.dataSource?.connection ?? {
        // XXX this is actually unused as we skip this query if draftEdits?.dataSource?.connection is undefined
        basic: null,
        bigQuery: null,
      },
      sqlTableName,
    },
    skip: !draftEdits?.isNew,
    onCompleted: (resp) =>
      onLoadColumnsCompleted(resp.dataModelListCdwTableColumns),
  });

  const loadingColumns = loadingSchemaColumns || loadingConnectionColumns;

  return {
    schemaObj,
    searchQuery,
    setSearchQuery,
    serverError,
    availableColumnsFromServer,
    loadingColumns,
  };
};

export default useDataModelerTableExpandedModel;
